import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Star as StarIcon,
  LocationOn as LocationOnIcon,
  Sell as SellIcon,
  HomeWork as HomeWorkIcon,
  Search as SearchIcon,
  Apartment as ApartmentIcon,
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { formatGBP, getRoomTypes, getRoomRates, getImageUrl, getPropertyListings, getPropertyTypes, getPropertyLocations } from '../services/api';
import Seo from '../components/Seo';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredRooms, setFeaturedRooms] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSearchTab, setActiveSearchTab] = useState('rent');
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [propertyLocations, setPropertyLocations] = useState([]);
  const [propertySearch, setPropertySearch] = useState({
    category: 'rent',
    propertyType: 'all',
    location: 'all',
    search: '',
    minPrice: '',
    maxPrice: '',
  });

  // Hero slider images (useMemo to keep dependency stable)
  const heroImages = useMemo(
    () => [
      {
        url: getImageUrl('Slider1.jpg', 'logos'),
        title: 'JCP Estate Agents',
        subtitle: 'Experience elegant stays in the heart of Plymouth',
      },
      {
        url: getImageUrl('slider2.jpg', 'logos'),
        title: 'Premium Rooms & Suites',
        subtitle: 'Comfort, character, and timeless hospitality',
      },
      {
        url: getImageUrl('slider3.jpg', 'logos'),
        title: 'Memorable Escapes',
        subtitle: 'Discover comfort and style at Grosvenor',
      },
    ],
    []
  );

  const propertyExploreItems = useMemo(() => {
    const allProperties = Array.isArray(featuredProperties) ? featuredProperties : [];
    const buyCount = allProperties.filter((property) => String(property.category || property.purpose || '').trim().toLowerCase() === 'buy').length;
    const sellCount = allProperties.filter((property) => String(property.category || property.purpose || '').trim().toLowerCase() === 'sell').length;
    const rentCount = allProperties.filter((property) => String(property.category || property.purpose || '').trim().toLowerCase() === 'rent').length;
    const uniqueAreas = new Set(
      allProperties
        .flatMap((property) => [property.location, property.city, property.state, property.region])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    );
    const priceValues = allProperties.map((property) => Number(property.price || 0)).filter((value) => value > 0);
    const minPrice = priceValues.length ? Math.min(...priceValues) : null;
    const maxPrice = priceValues.length ? Math.max(...priceValues) : null;

    const topArea = Array.from(uniqueAreas)[0] || '';

    return [
      {
        title: 'Buy Property',
        description: buyCount ? `${buyCount} featured buy properties ready to explore.` : 'Browse residential and commercial listings across prime UK locations.',
        icon: <ApartmentIcon />,
        path: '/properties?category=buy',
        accent: '#f4b740',
        bg: '#fff8e8',
      },
      {
        title: 'Sell Property',
        description: sellCount ? `${sellCount} featured sell properties ready to explore.` : 'Discover sale listings and premium property opportunities with ease.',
        icon: <SellIcon />,
        path: '/properties?category=sell',
        accent: '#60a5fa',
        bg: '#eef6ff',
      },
      {
        title: 'Rent Property',
        description: rentCount ? `${rentCount} rental properties available across selected areas.` : 'Find apartments, villas and family homes available for rent.',
        icon: <HomeWorkIcon />,
        path: '/properties?category=rent',
        accent: '#4ade80',
        bg: '#ecfdf3',
      },
      {
        title: 'Property Trends',
        description: allProperties.length ? `${allProperties.length} featured properties across active categories.` : 'Track locations and property categories that are growing in demand.',
        icon: <TrendingUpIcon />,
        path: '/properties?sortBy=featured',
        accent: '#a78bfa',
        bg: '#f4efff',
      },
      {
        title: 'Price Guide',
        description: minPrice && maxPrice ? `Current featured prices range from ${formatGBP(minPrice)} to ${formatGBP(maxPrice)}.` : 'Compare prices quickly before exploring detailed property results.',
        icon: <CalculateIcon />,
        path: '/properties?sortBy=priceLowHigh',
        accent: '#fb7185',
        bg: '#fff1f3',
      },
      {
        title: 'Area Finder',
        description: uniqueAreas.size ? `${uniqueAreas.size} highlighted areas available to search.` : 'Search cities, counties and regions to find the right area faster.',
        icon: <MapIcon />,
        path: topArea ? `/properties?location=${encodeURIComponent(topArea.toLowerCase())}` : '/properties',
        accent: '#34d399',
        bg: '#ecfeff',
      },
    ];
  }, [featuredProperties]);

  const fallbackBuyProperties = useMemo(
    () => {
      const prioritized = featuredProperties.filter((property) => {
        const category = String(property.category || property.purpose || '').trim().toLowerCase();
        return category === 'buy';
      });
      const fillers = featuredProperties.filter((property) => !prioritized.some((item) => item.id === property.id));
      return [...prioritized, ...fillers].slice(0, 3);
    },
    [featuredProperties]
  );

  const fallbackSellProperties = useMemo(
    () => {
      const prioritized = featuredProperties.filter((property) => {
        const category = String(property.category || property.purpose || '').trim().toLowerCase();
        return category === 'sell';
      });
      const fillers = featuredProperties.filter((property) => !prioritized.some((item) => item.id === property.id));
      return [...prioritized, ...fillers].slice(0, 3);
    },
    [featuredProperties]
  );

  const normalizedPropertyLocations = useMemo(() => {
    const valuesMap = new Map();

    const pushValue = (rawValue) => {
      const value = String(rawValue || '').trim();
      const normalized = value.toLowerCase();
      if (!normalized || normalized === 'all') return;
      if (!valuesMap.has(normalized)) {
        valuesMap.set(normalized, {
          value: normalized,
          label: value,
        });
      }
    };

    propertyLocations.forEach((item) => {
      [item?.name, item?.city, item?.state, item?.county, item?.region, item?.country].forEach(pushValue);
    });

    featuredProperties.forEach((item) => {
      [item?.location, item?.city, item?.state, item?.county, item?.region, item?.country, item?.postcode, item?.postalCode].forEach(pushValue);
    });

    return Array.from(valuesMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [featuredProperties, propertyLocations]);

  // Fetch featured rooms with pricing from API
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);
        const [roomTypesData, roomRatesData, propertyListingsData, propertyTypesData, propertyLocationsData] = await Promise.all([
          getRoomTypes(),
          getRoomRates(),
          getPropertyListings({ featuredOnly: true, pageSize: 3 }),
          getPropertyTypes(),
          getPropertyLocations(),
        ]);

        const roomTypes = Array.isArray(roomTypesData) ? roomTypesData : roomTypesData?.data || [];
        const roomRates = Array.isArray(roomRatesData) ? roomRatesData : roomRatesData?.data || [];
        const propertyListings = Array.isArray(propertyListingsData) ? propertyListingsData : propertyListingsData?.data || [];
        const propertyTypesRows = Array.isArray(propertyTypesData) ? propertyTypesData : propertyTypesData?.data || [];
        const propertyLocationsRows = Array.isArray(propertyLocationsData) ? propertyLocationsData : propertyLocationsData?.data || [];

        const toDateOnly = (v) => {
          if (!v) return null;
          const d = new Date(v);
          return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
        };

        const isWithin = (date, from, to) => {
          if (!date) return true;
          if (from && date < from) return false;
          if (to && date > to) return false;
          return true;
        };

        const getRateRoomTypeId = (rate) => {
          if (!rate) return null;
          return (
            rate.roomTypeId ??
            rate.RoomTypeId ??
            rate.roomType?.id ??
            rate.roomType?.Id ??
            rate.RoomType?.Id ??
            null
          );
        };

        const todayDate = toDateOnly(new Date());

        const roomsWithPricing = roomTypes
          .map((room) => {
            const roomNameKey = (room.roomTypeName || room.name || '').toString().toLowerCase();
            const roomTypeId = room.roomTypeId ?? room.RoomTypeId ?? room.roomType?.id ?? room.roomType?.Id ?? null;

            const matchingRates = roomRates
              .filter((rate) => {
                const rateRoomTypeId = getRateRoomTypeId(rate);
                if (roomTypeId && rateRoomTypeId) return Number(rateRoomTypeId) === Number(roomTypeId);

                const rateRoomType = rate?.roomType;
                const rateKey =
                  typeof rateRoomType === 'string'
                    ? rateRoomType.toLowerCase()
                    : (rateRoomType?.roomTypeName ||
                        rateRoomType?.name ||
                        rate.RoomType?.Name ||
                        '')
                        .toString()
                        .toLowerCase();

                return rateKey && rateKey === roomNameKey;
              })
              .filter((rate) => {
                const from = toDateOnly(rate.effectiveFrom || rate.EffectiveFrom || rate.validFrom);
                const to = toDateOnly(rate.effectiveTo || rate.EffectiveTo || rate.validTo);
                return isWithin(todayDate, from, to);
              });

            const rateInfo = matchingRates[0];
            if (!rateInfo) return null;

            const basePrice = rateInfo?.baseRate ?? rateInfo?.BaseRate ?? 0;
            const includesTax = (rateInfo?.includesTax ?? rateInfo?.IncludesTax) === true;
            const rateTaxPctRaw = rateInfo?.taxPercentage ?? rateInfo?.TaxPercentage;
            const taxRate =
              typeof rateTaxPctRaw === 'number' ? rateTaxPctRaw / 100 : parseFloat(rateTaxPctRaw || '0') / 100;

            const computedTaxRate = taxRate > 0 ? taxRate : 0;
            const taxAmount = includesTax ? 0 : basePrice * computedTaxRate;
            const totalWithTax = includesTax ? basePrice : basePrice + taxAmount;

            return {
              ...room,
              basePrice,
              weekendRate: rateInfo?.weekendRate ?? rateInfo?.WeekendRate ?? null,
              seasonalRate: rateInfo?.seasonalRate ?? rateInfo?.SeasonalRate ?? null,
              season: rateInfo?.season ?? rateInfo?.Season ?? null,
              currency: rateInfo?.currency ?? rateInfo?.Currency ?? 'GBP',
              includesBreakfast: (rateInfo?.includesBreakfast ?? rateInfo?.IncludesBreakfast) ?? false,
              includesTax,
              taxRate,
              taxAmount,
              totalWithTax,
              validFrom: rateInfo?.effectiveFrom ?? rateInfo?.EffectiveFrom ?? null,
              validTo: rateInfo?.effectiveTo ?? rateInfo?.EffectiveTo ?? null,
              rateName: rateInfo?.rateName ?? rateInfo?.RateName ?? room.roomTypeName,
              rateCode: rateInfo?.rateCode ?? rateInfo?.RateCode ?? null,
              price: basePrice,
            };
          })
          .filter(Boolean)
          .slice(0, 3);

        setFeaturedRooms(roomsWithPricing);
        setFeaturedProperties(propertyListings.slice(0, 3));
        setPropertyTypes(propertyTypesRows);
        setPropertyLocations(propertyLocationsRows);
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
        // Fallback to mock data if API fails
        setFeaturedRooms([
          {
            roomTypeId: 1,
            roomTypeName: 'Standard Room',
            basePrice: 5000,
            weekendRate: 6000,
            images: [
              'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            ],
            rating: 4.5,
            amenities: ['Free WiFi', 'AC', 'Room Service'],
          },
          {
            roomTypeId: 2,
            roomTypeName: 'Deluxe Room',
            basePrice: 8000,
            weekendRate: 9500,
            images: [
              'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            ],
            rating: 4.7,
            amenities: ['Free WiFi', 'AC', 'Balcony', 'Room Service'],
          },
          {
            roomTypeId: 3,
            roomTypeName: 'Executive Suite',
            basePrice: 15000,
            weekendRate: 17500,
            images: [
              'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            ],
            rating: 4.9,
            amenities: ['Free WiFi', 'AC', 'Balcony', 'Room Service', 'Living Area'],
          },
        ]);
        setFeaturedProperties([]);
        setPropertyTypes([]);
        setPropertyLocations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  const getPropertyImage = (property) => {
    return getImageUrl(
      property?.primaryImagePath ||
      property?.images?.find?.((img) => img.isPrimary)?.imagePath ||
      property?.images?.[0]?.imagePath ||
      '/Gallery/Items/lobby-1.jpg'
    );
  };

  const buyFeaturedProperties = useMemo(
    () => featuredProperties.filter((property) => String(property.category || property.purpose || '').trim().toLowerCase() === 'buy').slice(0, 3),
    [featuredProperties]
  );

  const sellFeaturedProperties = useMemo(
    () => featuredProperties.filter((property) => String(property.category || property.purpose || '').trim().toLowerCase() === 'sell').slice(0, 3),
    [featuredProperties]
  );

  // Blog posts
  const blogPosts = useMemo(
    () => [
      {
        id: 1,
        title: 'Top 10 Tourist Destinations in United Kingdom',
        excerpt: 'Discover the most beautiful places to visit during your stay...',
        image:
          'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        date: '2024-01-15',
        author: 'Travel Team',
      },
      {
        id: 2,
        title: 'Luxury Dining Experience at Our Restaurant',
        excerpt: 'Experience world-class cuisine prepared by our master chefs...',
        image:
          'https://images.unsplash.com/photo-1585937421612-70a008356fbe?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        date: '2024-01-12',
        author: 'Culinary Team',
      },
      {
        id: 3,
        title: 'Wedding Packages and Event Planning',
        excerpt: 'Make your special day unforgettable with our premium services...',
        image:
          'https://images.unsplash.com/photo-1606800052052-a08af7148866?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        date: '2024-01-10',
        author: 'Events Team',
      },
    ],
    []
  );

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);

  const handlePropertySearchChange = (field) => (event) => {
    setPropertySearch((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSearchTabChange = (tab) => {
    setActiveSearchTab(tab);
    if (tab === 'buy' || tab === 'sell') {
      setPropertySearch((prev) => ({ ...prev, category: tab }));
    }
  };

  const handlePropertySearchSubmit = (event) => {
    event.preventDefault();
    const query = new URLSearchParams({
      category: propertySearch.category,
      propertyType: propertySearch.propertyType,
      location: propertySearch.location,
      search: propertySearch.search,
      minPrice: propertySearch.minPrice,
      maxPrice: propertySearch.maxPrice,
    });
    navigate(`/properties?${query.toString()}`);
  };

  return (
    <Box>
      <Seo
        title="Home"
        description="Grosvenor Plymouth - Experience elegant hospitality with premium rooms, thoughtful service, and a memorable stay in Plymouth."
        keywords="Grosvenor Plymouth, Plymouth hotel, premium accommodation, hotel booking, luxury suites, hospitality"
        author="Grosvenor Plymouth"
      />

      {/* Hero wrapper */}
      <Box sx={{ minHeight: '100vh' }}>
        <Box
          sx={{
            position: 'relative',
            height: { xs: '80vh', sm: '90vh', md: '100vh' },
            overflow: 'hidden',
          }}
        >
          {heroImages.map((image, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: index === currentSlide ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
              }}
            >
              <Box
                component="img"
                src={image.url}
                alt={image.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(135deg, rgba(44, 90, 160, 0.7) 0%, rgba(30, 58, 111, 0.8) 50%, rgba(0, 0, 0, 0.6) 100%)',
                }}
              />
            </Box>
          ))}

          {/* Hero Content */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Container maxWidth="xl" sx={{ px: 3 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  color: 'white',
                  px: { xs: 2, sm: 3, md: 2 },
                  mb: { xs: 4, md: 6 },
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: 'Playfair Display',
                    fontWeight: 700,
                    fontSize: { xs: '2rem', sm: '2.8rem', md: '4rem', lg: '5rem' },
                    mb: { xs: 1, md: 2 },
                    textShadow: '3px 3px 6px rgba(0,0,0,0.7)',
                    background: 'linear-gradient(45deg, #ffffff 30%, #e6c659 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'fadeInUp 1s ease-out',
                    lineHeight: { xs: 1.2, md: 1.1 },
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(30px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  {heroImages[currentSlide].title}
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.4rem' },
                    mb: 4,
                    opacity: 0.95,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    animation: 'fadeInUp 1s ease-out 0.3s both',
                    maxWidth: '600px',
                    mx: 'auto',
                    lineHeight: 1.4,
                  }}
                >
                  {heroImages[currentSlide].subtitle}
                </Typography>
              </Box>
 
              <Box sx={{ animation: 'fadeInUp 1s ease-out 0.6s both', mb: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.75, sm: 3.5, md: 4 },
                    borderRadius: { xs: 4, md: 5 },
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    maxWidth: { xs: '100%', md: '1280px' },
                    mx: 'auto',
                    overflow: 'hidden',
                  }}
                >
                  <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} justifyContent="center" sx={{ mb: { xs: 2, sm: 3 }, flexWrap: 'wrap' }} useFlexGap>
                    {['rent', 'buy', 'sell'].map((tab) => (
                      <Button
                        key={tab}
                        variant={activeSearchTab === tab ? 'contained' : 'outlined'}
                        onClick={() => handleSearchTabChange(tab)}
                        sx={{ textTransform: 'capitalize', borderRadius: 999, minWidth: { xs: '100%', sm: 110 }, flex: { xs: '1 1 100%', sm: '0 0 auto' }, fontWeight: 700 }}
                      >
                        {tab}
                      </Button>
                    ))}
                  </Stack>

                  <Box component="form" onSubmit={handlePropertySearchSubmit}>
                      <Typography
                        variant="h4"
                        component="h3"
                        sx={{
                          fontFamily: 'Playfair Display',
                          fontWeight: 700,
                          textAlign: 'center',
                          mb: { xs: 2.5, md: 4 },
                          color: 'text.primary',
                          background: 'linear-gradient(45deg, #2c5aa0 30%, #d4af37 90%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontSize: { xs: '1.45rem', sm: '1.8rem', md: '2.2rem' },
                        }}
                      >
                        Find Your Perfect Property
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'minmax(210px, 2.6fr) minmax(120px, 1.35fr) minmax(135px, 1.55fr) minmax(145px, 1.7fr) minmax(130px, 1.45fr) minmax(130px, 1.45fr) minmax(120px, 1.25fr)',
                          },
                          gap: { xs: 1.5, sm: 2, md: 1.5 },
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <TextField
                            fullWidth
                            label="Search"
                            value={propertySearch.search}
                            onChange={handlePropertySearchChange('search')}
                            placeholder="Title or location"
                            size="medium"
                            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'primary.main' }} /> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, minHeight: { xs: 48, lg: 56 } } }}
                          />
                        </Box>
                        <Box>
                          <FormControl fullWidth size="medium">
                            <InputLabel>Purpose</InputLabel>
                            <Select value={propertySearch.category} label="Purpose" onChange={handlePropertySearchChange('category')} sx={{ borderRadius: 2.5, minHeight: { xs: 48, lg: 56 } }}>
                              <MenuItem value="buy">Buy</MenuItem>
                              <MenuItem value="sell">Sell</MenuItem>
                              <MenuItem value="rent">Rent</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Box>
                          <FormControl fullWidth size="medium">
                            <InputLabel>Type</InputLabel>
                            <Select value={propertySearch.propertyType} label="Type" onChange={handlePropertySearchChange('propertyType')} sx={{ borderRadius: 2.5, minHeight: { xs: 48, lg: 56 } }}>
                              <MenuItem value="all">All Types</MenuItem>
                              {propertyTypes.map((item) => (
                                <MenuItem key={item.id} value={String(item.code || item.name || '').toLowerCase()}>{item.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box>
                          <FormControl fullWidth size="medium">
                            <InputLabel>Location</InputLabel>
                            <Select value={propertySearch.location} label="Location" onChange={handlePropertySearchChange('location')} sx={{ borderRadius: 2.5, minHeight: { xs: 48, lg: 56 } }}>
                              <MenuItem value="all">All Locations</MenuItem>
                              {normalizedPropertyLocations.map((item) => (
                                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            label="Min Price"
                            type="number"
                            size="medium"
                            value={propertySearch.minPrice}
                            onChange={handlePropertySearchChange('minPrice')}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, minHeight: { xs: 48, lg: 56 } } }}
                          />
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            label="Max Price"
                            type="number"
                            size="medium"
                            value={propertySearch.maxPrice}
                            onChange={handlePropertySearchChange('maxPrice')}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, minHeight: { xs: 48, lg: 56 } } }}
                          />
                        </Box>
                        <Box>
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{
                              height: { xs: '48px', lg: '56px' },
                              fontWeight: 700,
                              borderRadius: 2.5,
                              background: 'linear-gradient(45deg, #2c5aa0 30%, #5a7bc8 90%)',
                              textTransform: 'none',
                            }}
                          >
                            Search
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                </Paper>
              </Box>
            </Container>
          </Box>

          {/* Slider Controls */}
          <IconButton
            onClick={prevSlide}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
              zIndex: 20,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={nextSlide}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
              zIndex: 20,
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Slider Indicators */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 20,
            }}
          >
            {heroImages.map((_, index) => (
              <Box
                key={index}
                component="button"
                onClick={() => setCurrentSlide(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: index === currentSlide ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
      {/* END hero wrapper */}

      <Box sx={{ py: { xs: 6, md: 8 }, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h2" sx={{ fontFamily: 'Playfair Display', fontWeight: 700, color: 'text.primary', mb: 2, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
              Property Services
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 760, mx: 'auto' }}>
              Buy, sell and rent homes, villas, offices and plots through our new multi-location property platform.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {propertyExploreItems.map((item) => (
              <Card key={item.title} sx={{ borderRadius: 4, boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)', border: '1px solid rgba(15, 23, 42, 0.06)' }}>
                <CardContent sx={{ p: 3.25 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: 3, bgcolor: item.bg, color: item.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>{item.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{item.description}</Typography>
                      <Button component={Link} to={item.path} variant="text" endIcon={<ArrowForwardIcon />} sx={{ px: 0, fontWeight: 700, textTransform: 'none' }}>
                        Explore More
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#eef4ff' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h2" sx={{ fontFamily: 'Playfair Display', fontWeight: 700, color: 'text.primary', mb: 1, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
                Featured Properties
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 700 }}>
                Handpicked listings across multiple property purposes and locations.
              </Typography>
            </Box>
            <Button component={Link} to="/properties" variant="contained" endIcon={<ArrowForwardIcon />}>
              View All Properties
            </Button>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {featuredProperties.length === 0 && !loading ? (
              <Card sx={{ gridColumn: '1 / -1', borderRadius: 4 }}>
                <CardContent sx={{ p: 5, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Featured properties will appear here</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add featured property listings from the admin panel to highlight them on the homepage.
                  </Typography>
                  <Button component={Link} to="/properties" variant="outlined">Browse Properties</Button>
                </CardContent>
              </Card>
            ) : null}

            {featuredProperties.map((property) => (
              <Card key={property.id} sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
                <CardMedia component="img" height="240" image={getPropertyImage(property)} alt={property.title} sx={{ objectFit: 'cover' }} />
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" label={property.category || property.purpose || 'Property'} color="primary" />
                    <Chip size="small" label={property.propertyType || 'Type'} variant="outlined" />
                    {property.isFeatured ? <Chip size="small" label="Featured" color="secondary" /> : null}
                  </Box>

                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{property.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, minHeight: 42 }}>
                    {property.subtitle || property.description || 'Premium property listing'}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
                    <LocationOnIcon fontSize="small" />
                    <Typography variant="body2">{property.location}{property.city ? `, ${property.city}` : ''}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Starting from</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        {formatGBP(property.price || 0)}
                      </Typography>
                    </Box>
                    <Button component={Link} to={`/properties/${property.id}`} variant="contained">
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontFamily: 'Playfair Display', fontWeight: 700, mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                    Buy Properties
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Explore homes and investment opportunities available for purchase.
                  </Typography>
                </Box>
                <Button component={Link} to="/properties?category=buy" variant="outlined" endIcon={<ArrowForwardIcon />}>
                  View Buy Properties
                </Button>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                {(buyFeaturedProperties.length ? buyFeaturedProperties : fallbackBuyProperties).map((property) => (
                  <Card key={`buy-${property.id}`} sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
                    <CardMedia component="img" height="220" image={getPropertyImage(property)} alt={property.title} sx={{ objectFit: 'cover' }} />
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip size="small" label="Buy" color="primary" />
                        <Chip size="small" label={property.propertyType || 'Type'} variant="outlined" />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{property.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, minHeight: 40 }}>
                        {property.subtitle || property.description || 'Premium property listing'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
                        <LocationOnIcon fontSize="small" />
                        <Typography variant="body2">{property.location}{property.city ? `, ${property.city}` : ''}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>{formatGBP(property.price || 0)}</Typography>
                        <Button component={Link} to={`/properties/${property.id}`} variant="contained">View</Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {!((buyFeaturedProperties.length ? buyFeaturedProperties : fallbackBuyProperties).length) ? (
                  <Card sx={{ borderRadius: 4, gridColumn: '1 / -1' }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Buy properties will appear here</Typography>
                      <Button component={Link} to="/properties?category=buy" variant="outlined">Browse Buy Properties</Button>
                    </CardContent>
                  </Card>
                ) : null}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontFamily: 'Playfair Display', fontWeight: 700, mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                    Sell Properties
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Showcase premium listings available for sale and discovery.
                  </Typography>
                </Box>
                <Button component={Link} to="/properties?category=sell" variant="outlined" endIcon={<ArrowForwardIcon />}>
                  View Sell Properties
                </Button>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                {(sellFeaturedProperties.length ? sellFeaturedProperties : fallbackSellProperties).map((property) => (
                  <Card key={`sell-${property.id}`} sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
                    <CardMedia component="img" height="220" image={getPropertyImage(property)} alt={property.title} sx={{ objectFit: 'cover' }} />
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip size="small" label="Sell" color="secondary" />
                        <Chip size="small" label={property.propertyType || 'Type'} variant="outlined" />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{property.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, minHeight: 40 }}>
                        {property.subtitle || property.description || 'Premium property listing'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
                        <LocationOnIcon fontSize="small" />
                        <Typography variant="body2">{property.location}{property.city ? `, ${property.city}` : ''}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>{formatGBP(property.price || 0)}</Typography>
                        <Button component={Link} to={`/properties/${property.id}`} variant="contained">View</Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {!((sellFeaturedProperties.length ? sellFeaturedProperties : fallbackSellProperties).length) ? (
                  <Card sx={{ borderRadius: 4, gridColumn: '1 / -1' }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Sell properties will appear here</Typography>
                      <Button component={Link} to="/properties?category=sell" variant="outlined">Browse Sell Properties</Button>
                    </CardContent>
                  </Card>
                ) : null}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Featured Rooms Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontFamily: 'Playfair Display',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Featured Property, Studio, Villa, Apartments
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
              Discover our most popular accommodations, designed for comfort and luxury
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 300,
                  gridColumn: '1 / -1',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              featuredRooms.map((room) => {
                const amenities = Array.isArray(room.amenities) ? room.amenities : [];
                return (
                  <Card
                    key={room.roomTypeId ?? room.id ?? room.roomTypeName}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', paddingTop: '60%', backgroundColor: '#f5f5f5' }}>
                      {room.images && room.images.length > 0 ? (
                        <CardMedia
                          component="img"
                          image={getImageUrl(room.images[0], 'roomtypes')}
                          alt={room.roomTypeName}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            color: '#9ca3af',
                          }}
                        >
                          <Typography variant="body2">No Image</Typography>
                        </Box>
                      )}

                      <Chip
                        label={room.roomTypeCode || 'Room'}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          backgroundColor: '#2563eb',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                        }}
                      />

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        <StarIcon sx={{ fontSize: 14 }} />
                        {room.rating || 4.5}
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#111827', lineHeight: 1.3 }}>
                        {room.roomTypeName}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.5, flexGrow: 1 }}>
                        {room.description || 'Luxurious accommodation with modern amenities'}
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {amenities.slice(0, 3).map((amenity, index) => (
                            <Chip
                              key={`${amenity}-${index}`}
                              label={amenity}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: '#e5e7eb',
                                color: '#6b7280',
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                          ))}
                          {amenities.length > 3 && (
                            <Chip
                              label={`+${amenities.length - 3} more`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: '#e5e7eb',
                                color: '#6b7280',
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {room.location || ''}
                          </Typography>
                          <Typography variant="h6" fontWeight="700" sx={{ color: '#2563eb' }}>
                            {formatGBP(Number(room.basePrice || 0))}/night
                          </Typography>
                        </Box>

                        <Button
                          onClick={() => {
                            const today = new Date();
                            const tomorrow = new Date(today);
                            tomorrow.setDate(tomorrow.getDate() + 1);

                            const searchParams = new URLSearchParams({
                              location: 'all',
                              checkIn: today.toISOString().split('T')[0],
                              checkOut: tomorrow.toISOString().split('T')[0],
                              adults: '1',
                              children: '0',
                              rooms: '1',
                              category: room.roomTypeCode || room.roomTypeName,
                            });

                            navigate(`/search?${searchParams.toString()}`);
                          }}
                          variant="outlined"
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            borderColor: '#2563eb',
                            color: '#2563eb',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            width: '100%',
                            '&:hover': { backgroundColor: '#2563eb', color: 'white' },
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Box>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button component={Link} to="/rooms" variant="contained" color="primary" size="large" endIcon={<ArrowForwardIcon />}>
              View All Rooms
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Blog Section */}
      <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ fontFamily: 'Playfair Display', fontWeight: 700, color: 'text.primary', mb: 2 }}>
              Latest News & Updates
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
              Stay updated with our latest news, travel tips, and special offers
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {blogPosts.map((post) => (
              <Card
                key={post.id}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)' },
                }}
              >
                <Box sx={{ position: 'relative', paddingTop: '60%', backgroundColor: '#f5f5f5' }}>
                  <CardMedia
                    component="img"
                    image={post.image}
                    alt={post.title}
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Chip
                    label={post.author.replace(' Team', '')}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: '#2563eb',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{
                      mb: 1.5,
                      color: '#111827',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      mb: 2.5,
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.excerpt}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {post.author}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        •
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {new Date(post.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    component={Link}
                    to={`/blog/${post.id}`}
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      mt: 'auto',
                      borderColor: '#2563eb',
                      color: '#2563eb',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#2563eb', color: 'white' },
                    }}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Travel Blog Section */}
      <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontFamily: 'Playfair Display' }}>
              Travel Blog & Stories
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto', mb: 4 }}>
              Discover United Kingdom&apos;s hidden gems, cultural treasures, and culinary delights through our travel stories
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {[
              {
                id: 1,
                title: 'Top 10 Tourist Destinations in United Kingdom',
                excerpt:
                  'Discover the most beautiful places to visit during your stay in United Kingdom, from historic cities to scenic coastlines.',
                image:
                  'https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
                category: 'Travel Tips',
                author: 'Sarah Ahmed',
                date: '2024-01-15',
                readTime: '8 min read',
              },
              {
                id: 2,
                title: 'A Culinary Journey Through Pakistani Cuisine',
                excerpt:
                  'Experience the rich flavors and diverse culinary traditions that make Pakistani food unique and unforgettable.',
                image:
                  'https://images.unsplash.com/photo-1585937421612-70a008356fbe?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
                category: 'Food & Dining',
                author: 'Chef Muhammad Ali',
                date: '2024-01-12',
                readTime: '6 min read',
              },
              {
                id: 3,
                title: 'The Majestic Beauty of Northern United Kingdom',
                excerpt:
                  'Explore the stunning highlands, pristine lakes, and breathtaking valleys across the United Kingdom.',
                image:
                  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
                category: 'Adventure',
                author: 'Ahmad Hassan',
                date: '2024-01-10',
                readTime: '10 min read',
              },
            ].map((post) => (
              <Card
                key={post.id}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)' },
                }}
              >
                <Box sx={{ position: 'relative', paddingTop: '60%', backgroundColor: '#f5f5f5' }}>
                  <CardMedia
                    component="img"
                    image={post.image}
                    alt={post.title}
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Chip
                    label={post.category}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: '#2563eb',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{
                      mb: 1.5,
                      color: '#111827',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      mb: 2.5,
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.excerpt}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {post.author}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        •
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {new Date(post.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      {post.readTime}
                    </Typography>
                  </Box>

                  <Button
                    component={Link}
                    to={`/blog/${post.id}`}
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      mt: 'auto',
                      borderColor: '#2563eb',
                      color: '#2563eb',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#2563eb', color: 'white' },
                    }}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              component={Link}
              to="/blogs"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#2563eb',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              View All Blog Posts
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, backgroundColor: 'primary.dark', color: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontFamily: 'Playfair Display', fontWeight: 700, mb: 3 }}>
              Ready to Experience Luxury?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Book your stay today and enjoy world-class hospitality across United Kingdom
            </Typography>
            <Button component={Link} to="/reservation" variant="contained" color="secondary" size="large" sx={{ px: 4, py: 1.5 }}>
              Book Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
